import { t } from '@glory/i18n';
import { Badge, Button, Callout, cn, Dialog, EmptyState, InfoHint, Segments } from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Inbox, Rows3, Zap } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useEffect } from 'react';

import { type LeadsSearch, parseLeadsSearch } from '../../../features/sales/model/filters.ts';
import { useListActions } from '../../../features/sales/runtime/actions.ts';
import { DealPanel } from '../../../features/sales/ui/deal-panel.tsx';
import { LeadTable } from '../../../features/sales/ui/lead-table.tsx';
import { QuickLead } from '../../../features/sales/ui/quick-lead.tsx';
import { QuickStartDialog } from '../../../features/sales/ui/quick-start-dialog.tsx';
import { SalesHeader } from '../../../features/sales/ui/sales-tabs.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/sales/leads')({
  validateSearch: (search: Record<string, unknown>) => parseLeadsSearch(search),
  beforeLoad: requirePermissionRoute('sales.use'),
  component: LeadsPage,
});

type Filter = 'queue' | 'mine' | 'all';
const STATES: readonly LeadsSearch['state'][] = [undefined, 'postponed', 'rejected'];

/**
 * «Лиды»: строка быстрого ввода сверху (Enter — лид создан), фильтры «Очередь» (у того, кто её видит),
 * «Мои», «Все» и состояние (в работе, отложенные, отклонённые), таблица и панель сделки справа.
 */
function LeadsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { can } = usePermissions();
  const listActions = useListActions();
  const canQueue = can('sales.read_all');
  // «Быстрый расчёт» сразу ведёт лид в сделку и смету — только тому, кто ведёт сделки сам.
  const canQuickCalc = can('sales.lead');
  const filter: Filter = search.filter ?? 'all';
  // Очередь видят только те, у кого `sales.read_all`: у остальных ссылка на неё открывает «Все».
  const effective: Filter = filter === 'queue' && !canQueue ? 'all' : filter;

  const list = useQuery({
    ...orpc.sales.leads.list.queryOptions({
      input: {
        ...(effective === 'all' ? {} : { filter: effective }),
        ...(search.state === undefined ? {} : { state: search.state }),
      },
    }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  const queue = list.data?.queue ?? 0;
  const openIndex = items.findIndex((row) => row.id === search.deal);
  const filtered = effective !== 'all' || search.state !== undefined;

  // «Новый лид» из ⌘K: курсор уже в строке ввода — отметку из адреса убираем, чтобы ссылка осталась чистой.
  useEffect(() => {
    if (search.create !== true) return;
    void navigate({ search: (prev) => ({ ...prev, create: undefined }), replace: true });
  }, [search.create, navigate]);

  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: (prev) => ({ ...prev, deal: id }), replace });
  };
  const setFilter = (patch: Partial<Pick<LeadsSearch, 'filter' | 'state'>>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  const filterOptions: { value: Filter; label: string }[] = [
    ...(canQueue
      ? [
          {
            value: 'queue' as const,
            label: queue > 0 ? `${t('sales.filters.queue')} · ${String(queue)}` : t('sales.filters.queue'),
          },
        ]
      : []),
    { value: 'mine', label: t('sales.filters.mine') },
    { value: 'all', label: t('sales.filters.all') },
  ];

  const emptyText =
    effective === 'queue'
      ? { title: t('sales.leads.emptyQueueTitle'), text: t('sales.leads.emptyQueueText') }
      : filtered
        ? { title: t('sales.leads.emptyFilteredTitle'), text: t('sales.leads.emptyFilteredText') }
        : { title: t('sales.leads.emptyTitle'), text: t('sales.leads.emptyText') };

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <SalesHeader />
      <div className="flex flex-col gap-2">
        <QuickLead autoFocus={search.create === true} />
        {canQuickCalc ? (
          <div className="flex">
            <Button
              onClick={() => {
                void navigate({ search: (prev) => ({ ...prev, quick: true }) });
              }}
            >
              <Zap /> {t('sales.quickCalc.open')}
            </Button>
          </div>
        ) : null}
      </div>
      <Dialog
        open={canQuickCalc && search.quick === true}
        onOpenChange={(open) => {
          if (!open) void navigate({ search: (prev) => ({ ...prev, quick: undefined }), replace: true });
        }}
      >
        {canQuickCalc && search.quick === true ? (
          <QuickStartDialog
            onCancel={() => {
              void navigate({ search: (prev) => ({ ...prev, quick: undefined }), replace: true });
            }}
          />
        ) : null}
      </Dialog>

      <div className="flex flex-wrap items-center gap-2">
        <Segments
          size="sm"
          aria-label={t('sales.tabLeads')}
          value={effective}
          options={filterOptions}
          onValueChange={(next) => {
            setFilter({ filter: next === 'all' ? undefined : next });
          }}
        />
        {effective === 'queue' ? (
          <InfoHint
            label={t('sales.filters.queue')}
            hint={{
              title: t('sales.filters.queue'),
              text: t('sales.hints.queue.text'),
              example: t('sales.hints.queue.example'),
            }}
          />
        ) : null}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('sales.leads.state')}>
          {STATES.map((state) => {
            const active = search.state === state;
            return (
              <button
                key={state ?? 'open'}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setFilter({ state });
                }}
                className={cn(
                  'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
                  active
                    ? 'border-strong bg-strong text-on-strong'
                    : 'border-border text-muted hover:text-text',
                )}
              >
                {t(`sales.leads.states.${state ?? 'open'}`)}
              </button>
            );
          })}
        </div>
        {queue > 0 && effective !== 'queue' && canQueue ? (
          <Badge tone="warn" className="ml-auto">
            {t('sales.leads.inQueue', { count: queue })}
          </Badge>
        ) : null}
      </div>

      {list.isError ? (
        <Callout className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1">{list.error.message}</span>
          <Button
            size="sm"
            onClick={() => {
              void list.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </Callout>
      ) : (
        <>
          <LeadTable
            items={items}
            loading={list.isPending}
            activeId={search.deal ?? null}
            canTake={canQueue}
            onOpen={(row) => {
              show(row.id, search.deal !== undefined);
            }}
            onTake={(row) => {
              void listActions.takeLead(row);
            }}
            empty={
              <EmptyState
                icon={effective === 'queue' ? <Inbox /> : <Rows3 />}
                title={emptyText.title}
                description={emptyText.text}
                {...(filtered
                  ? {
                      action: (
                        <Button
                          onClick={() => {
                            setFilter({ filter: undefined, state: undefined });
                          }}
                        >
                          {t('sales.resetFilters')}
                        </Button>
                      ),
                    }
                  : {})}
              />
            }
          />
          {list.data && list.data.total > items.length ? (
            <p className="text-small text-muted">
              {t('sales.moreHint', { count: items.length, total: list.data.total })}
            </p>
          ) : null}
        </>
      )}

      {/* Постоянный key: переход к соседней записи не закрывает и не открывает панель заново. */}
      <AnimatePresence initial={false}>
        {search.deal ? (
          <DealPanel
            key="panel"
            id={search.deal}
            onClose={() => {
              show(undefined, true);
            }}
            onPrevious={() => {
              const previous = items[openIndex - 1];
              if (previous) show(previous.id, true);
            }}
            onNext={() => {
              const next = items[openIndex + 1];
              if (next) show(next.id, true);
            }}
            onExpand={() => {
              void navigate({ to: '/sales/deals/$id', params: { id: search.deal ?? '' } });
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
